import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  HStack,
  Spinner,
  Flex,
  Button,
  Checkbox,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tooltip,
  IconButton,
  ModalFooter,
} from "@chakra-ui/react";
import { ChevronDownIcon, DeleteIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { RiSearchLine, RiInformationLine } from "react-icons/ri";
import { listScheduledJobs, getScheduledJob, deleteScheduledJob } from "../../services/ScheduleServices";
import { Header } from "build-ts/metadata";
import { format } from "date-fns";
import { getSortByLabel } from "../../utils/analysisUtils";
import { useUser } from "../../context/UserContext";
import cronstrue from "cronstrue";
import showToast from "hooks/useCustomToast";
import CustomModal from "components/modals/CustomModal";
import ReactMarkdown from "react-markdown";
import { epochConverterWithTimeZone } from "utils/dateUtils";

// Extend Header type for schedule-specific properties
interface ScheduleHeader extends Header {
  isActive?: boolean;
  cronExpression?: string;
}

const LibrarySchedulesComponent: React.FC = () => {
  const { defaultTimeZone } = useUser();

  const [scheduledJobs, setScheduledJobs] = useState<ScheduleHeader[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<ScheduleHeader[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchString, setSearchString] = useState("");
  const [sortBy, setSortBy] = useState("LAST_MODIFIED");
  const [sortAscending, setSortAscending] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const pageSize = 20;

  useEffect(() => {
    fetchAgents();
  }, [currentPage, sortBy, sortAscending]);

  // Filter jobs based on search string
  useEffect(() => {
    if (searchString.trim() === "") {
      setFilteredJobs(scheduledJobs);
    } else {
      const filtered = scheduledJobs.filter(job =>
        job.name?.toLowerCase().includes(searchString.toLowerCase()) ||
        job.description?.toLowerCase().includes(searchString.toLowerCase()) ||
        job.author_name?.toLowerCase().includes(searchString.toLowerCase())
      );
      setFilteredJobs(filtered);
    }
  }, [searchString, scheduledJobs]);

  // Convert cron expression to human readable format
  const cronToHumanReadable = (cronExpression: string | undefined): string => {
    if (!cronExpression) return "Not scheduled";
    return cronstrue.toString(cronExpression);
  };

  const fetchAgents = async () => {
    setIsLoading(true);
    try {
      const schedules = await listScheduledJobs(
        "",
        pageSize,
        currentPage,
        sortBy,
        sortAscending,
        true
      );

      // Cast to ScheduleHeader to include schedule-specific properties
      const scheduleHeaders = schedules.map((header: any) => ({
        ...Header.fromJSON(header),
        isActive: header.isActive,
        cronExpression: header.cronExpression || header.schedule || header.cron || null
      })) as ScheduleHeader[];

      // Fetch detailed schedule information for each job
      const enrichedHeaders = await Promise.all(
        scheduleHeaders.map(async (header) => {
          try {
            const detailedJob = await getScheduledJob(header.id!);
            if (detailedJob) {
              const cronExpr = detailedJob?.cron_expr || "";
              return {
                ...header,
                cronExpression: cronExpr
              };
            }
            return header;
          } catch (error) {
            console.error(`Error fetching details for job ${header.id}:`, error);
            return header;
          }
        })
      );

      setScheduledJobs(enrichedHeaders);
      setFilteredJobs(enrichedHeaders);
      setHasMorePages(schedules.length === pageSize);
    } catch (error) {
      console.error("Error fetching scheduled jobs:", error);
      showToast("Error fetching scheduled jobs", "Failed to load scheduled jobs", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSchedule = async (id: string) => {
    setOpenDeleteModal(true);
    setDeleteId(id);
  };

  const handleDelete = async () => {
    if (deleteId) {
      await deleteScheduledJob(deleteId);
      showToast("Schedule deleted successfully", "", "success");
      fetchAgents();
      setOpenDeleteModal(false);
      setDeleteId(null);
    }
  };

  const formatDate = (dateMs: number | undefined) => {
    if (!dateMs) return "N/A";
    try {
      return `${epochConverterWithTimeZone(dateMs || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)} ${defaultTimeZone.toUpperCase()}`;
    } catch {
      return "N/A";
    }
  };

  // Handle individual job selection
  const handleJobSelection = (jobId: string) => {
    const newSelectedJobs = new Set(selectedJobs);
    if (newSelectedJobs.has(jobId)) {
      newSelectedJobs.delete(jobId);
    }
    setSelectedJobs(newSelectedJobs);
  };

  // Handle select all/none
  const handleSelectAll = () => {
    if (selectedJobs.size === filteredJobs.length) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(filteredJobs.map(job => job.id!)));
    }
  };

  // Check if all jobs are selected
  const isAllSelected = filteredJobs.length > 0 && selectedJobs.size === filteredJobs.length;
  const isIndeterminate = selectedJobs.size > 0 && selectedJobs.size < filteredJobs.length;

  return (
    <Box p={6} height="100%" overflowY="auto">
      {/* Search and Filter Section */}
        <HStack spacing={4} mb={6} justify="space-between">
          <HStack spacing={4}>
            <InputGroup size="md" maxW="400px">
              <InputLeftElement>
                <Icon as={RiSearchLine} color="gray.500" />
              </InputLeftElement>
                          <Input
              placeholder="Search Schedules..."
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              focusBorderColor="purple.600"
            />
          </InputGroup>

          {/* Sort By Toggle */}
          <Flex align="center">
            <Text
              align="center"
              mb={0}
              mr={2}
              fontSize="sm"
              fontWeight="medium"
              color="fg.muted"
              whiteSpace="nowrap"
            >
              Sort by:
            </Text>
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon color="fg.muted" />}
                fontSize="xs"
                size="xs"
                rounded="md"
                color="fg.default"
                bg="bg.default"
                border="1px"
                borderColor="gray.300"
                _hover={{ bg: "purple.50" }}
                p={3}
                aria-label="Sort by"
                textAlign="left"
                width="150px"
                isTruncated
              >
                {getSortByLabel(sortBy)} {sortAscending ? "Asc" : "Desc"}
              </MenuButton>
              <MenuList>
                <MenuItem key="last_modified_asc"
                  onClick={() => {
                    setSortBy("LAST_MODIFIED");
                    setSortAscending(true);
                  }}
                  fontSize="small"
                >
                  Last Modified (Asc)
                </MenuItem>
                <MenuItem key="last_modified_desc"
                  onClick={() => {
                    setSortBy("LAST_MODIFIED");
                    setSortAscending(false);
                  }}
                  fontSize="small"
                >
                  Last Modified (Desc)
                </MenuItem>
                <MenuItem key="created_asc"
                  onClick={() => {
                    setSortBy("CREATED");
                    setSortAscending(true);
                  }}
                  fontSize="small"
                >
                  Created At (Asc)
                </MenuItem>
                <MenuItem key="created_desc"
                  onClick={() => {
                    setSortBy("CREATED");
                    setSortAscending(false);
                  }}
                  fontSize="small"
                >
                  Created At (Desc)
                </MenuItem>
                <MenuItem key="name_asc"
                  onClick={() => {
                    setSortBy("NAME");
                    setSortAscending(true);
                  }}
                  fontSize="small"
                >
                  Name (Asc)
                </MenuItem>
                <MenuItem key="name_desc"
                  onClick={() => {
                    setSortBy("NAME");
                    setSortAscending(false);
                  }}
                  fontSize="small"
                >
                  Name (Desc)
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>

          <Button
            colorScheme="purple"
            size="md"
            onClick={() => {
              // Open all selected scheduled jobs in new tabs
              selectedJobs.forEach(jobId => {
                window.open(`/scheduledJob?metadataId=${jobId}&metadataType=SCHEDULED_JOB`, "_blank");
              });
            }}
            isDisabled={selectedJobs.size === 0}
            _disabled={{
              opacity: 0.6,
              cursor: "not-allowed"
            }}
          >
            Open {selectedJobs.size > 0 ? `(${selectedJobs.size})` : ""}
          </Button>
        </HStack>

        <HStack spacing={4}>
          {selectedJobs.size > 0 && (
            <Text fontSize="sm" color="purple.600" fontWeight="medium">
              {selectedJobs.size} selected
            </Text>
          )}
          <Text fontSize="sm" color="fg.muted">
            {filteredJobs.length} results
          </Text>
        </HStack>
      </HStack>

      {/* Table */}
      <TableContainer
        bg="white"
        borderRadius="lg"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
      >
        {isLoading ? (
          <Flex justify="center" align="center" p={8}>
            <Spinner size="lg" color="purple.500" />
          </Flex>
        ) : (
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm" width="50px">
                  <Checkbox
                    isChecked={isAllSelected}
                    isIndeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                    colorScheme="purple"
                  />
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Job Name
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Description
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Schedule
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Author
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Created Date
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm" minW="80px">
                  Status
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  {/* for delete */}
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredJobs.length === 0 ? (
                <Tr>
                    <Td colSpan={7} textAlign="center" py={8} color="fg.muted">
                      {searchString.trim() !== "" ? "No Schedules match your search" : "No Schedules found"}
                  </Td>
                </Tr>
              ) : (
                filteredJobs.map((job) => (
                  <Tr
                    key={job.id}
                    _hover={{ bg: "gray.50" }}
                    bg={selectedJobs.has(job.id!) ? "purple.50" : "white"}
                  >
                    <Td>
                      <Checkbox
                        isChecked={selectedJobs.has(job.id!)}
                        onChange={() => handleJobSelection(job.id!)}
                        colorScheme="purple"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Td>
                    <Td
                      fontWeight="medium"
                      color="fg.default"
                      maxW="300px"
                      whiteSpace="normal"
                      wordBreak="break-word"
                    >
                      <HStack spacing={2} align="center" display="flex" alignItems="center">
                        <Text mb={0}>{job.name || "Unnamed Job"}</Text>
                        <ExternalLinkIcon
                          color="purple.500"
                          cursor="pointer"
                          _hover={{ color: "purple.700" }}
                          boxSize={4}
                          onClick={() => {
                            // Navigate to plan page
                            window.open(`/scheduledJob?metadataId=${job.id}&metadataType=SCHEDULED_JOB`, "_blank");
                          }}
                        />
                      </HStack>
                    </Td>
                    <Td
                      color="fg.muted"
                      maxW="400px"
                    >
                      <Tooltip
                        label={
                          <Box maxW="400px">
                            <ReactMarkdown>
                              {job.description || ""}
                            </ReactMarkdown>
                          </Box>
                        }
                        hasArrow
                        placement="top"
                        isDisabled={!job.description}
                        bg="gray.700"
                        color="white"
                        fontSize="sm"
                      >
                        <Box
                          fontSize="sm"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                          cursor={job.description ? "pointer" : "default"}
                          sx={{
                            "& p": {
                              margin: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }
                          }}
                        >
                          {job.description === null || job.description === undefined || job.description === "" ? "" : job.description}
                        </Box>
                      </Tooltip>
                    </Td>
                    <Td
                      color="fg.muted"
                    >
                      <Flex align="center">
                        <Text fontSize="sm" fontWeight="medium" color="fg.default">
                          {cronToHumanReadable(job?.cronExpression)} ({defaultTimeZone})
                        </Text>
                        {job?.cronExpression && (
                          <Tooltip
                            label={`Cron Expression: ${job.cronExpression}`}
                            placement="top"
                            hasArrow
                            bg="gray.800"
                            color="white"
                            fontSize="sm"
                            p={2}
                            borderRadius="md"
                          >
                            <Box
                              as="span"
                              ml={2}
                              cursor="help"
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                              transform="translateY(-9px)"
                            >
                              <Icon as={RiInformationLine} color="gray.400" boxSize={4} />
                            </Box>
                          </Tooltip>
                        )}
                      </Flex>
                    </Td>
                    <Td
                      color="fg.muted"
                    >
                      {job.author_name || "Unknown"}
                    </Td>
                    <Td
                      color="fg.muted"
                    >
                      {formatDate(job.created_date_ms)}
                    </Td>
                    <Td
                    >
                      <Box
                        w="16px"
                        h="16px"
                        borderRadius="full"
                        bg={job.isActive ? "green.500" : "red.500"}
                        display="inline-block"
                        border="2px solid"
                        borderColor={job.isActive ? "green.600" : "red.600"}
                      />
                    </Td>
                    <Td>
                    <Tooltip label="Delete Schedule">
                        <IconButton
                          aria-label="Delete Schedule"
                          pl={4}
                          icon={<DeleteIcon />}
                          size="md"
                          colorScheme="red"
                          bg="none"
                          color="grey"
                          _hover={{ color: "red.500" }}
                          onClick={() => handleDeleteSchedule(job.id!)}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      {!isLoading && (filteredJobs.length > 0 || currentPage > 1) && searchString.trim() === "" && (
        <HStack justify="center" mt={6} spacing={4}>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            isDisabled={currentPage === 1}
          >
            Previous
          </Button>
          <Text fontSize="sm" color="fg.muted">
            Page {currentPage}
          </Text>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCurrentPage(currentPage + 1)}
            isDisabled={!hasMorePages}
          >
            Next
          </Button>
        </HStack>
      )}

      {/* Delete Modal */}
      <CustomModal
        isOpen={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        title="Are you sure want to delete the schedule?"
        size="sm"
        maxWidth="600px"
        maxHeight="200px"
        showCloseButton={false}
      >
        <ModalFooter>
          <Button colorScheme="red" mr={3} onClick={() => handleDelete()}>
            Delete
          </Button>
          <Button colorScheme="purple" onClick={() => setOpenDeleteModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </CustomModal>
    </Box>
  );
};

export default LibrarySchedulesComponent;